import { TRACKER_CATEGORY_OPTIONS } from 'src/services/consts/scan/scan.const'
import { TRACKER_CATEGORIES, TRACKER_CLASSIFICATION_STATUS } from 'src/services/consts/shared.const'

// Banners list / dashboard — table columns, stats period/trend.

/** Rolling window length for banner aggregate stats queries (`getBannerStatsDateRanges`). */
export const BANNER_STATS_PERIOD_DAYS = 30

/** Rolling window inline on banner index stat tile labels (`StatTile` `periodLabel`). */
export const BANNER_STATS_PERIOD_LABEL = 'Last 30 days'

/** Stat trend direction vs previous 30 days (`calcBannerStatsTrend` → `StatTile` `trend` prop). */
export const BANNER_STATS_TREND_DIRECTION = {
  UP: 'up',
  DOWN: 'down',
  FLAT: 'flat',
}

/**
 * Trend badge on banner stat cards: Lucide icon + `$ax-colors` utilities.
 * `darkBadgeClass` is used on `StatTile` `theme="dark"` (e.g. brand-blue dashboard card).
 * @type {Record<string, { iconName: string, badgeClass: string, darkBadgeClass: string }>}
 */
export const BANNER_STATS_TREND_BADGE_MAP = {
  [BANNER_STATS_TREND_DIRECTION.UP]: {
    iconName: 'arrow-up',
    badgeClass: 'bg-positive-light text-positive',
    darkBadgeClass: 'bg-surface-dark text-positive',
  },
  [BANNER_STATS_TREND_DIRECTION.DOWN]: {
    iconName: 'arrow-down',
    badgeClass: 'bg-negative-light text-negative',
    darkBadgeClass: 'bg-surface-dark text-negative-soft',
  },
  [BANNER_STATS_TREND_DIRECTION.FLAT]: {
    iconName: 'minus',
    badgeClass: 'bg-border-light text-secondary',
    darkBadgeClass: 'bg-border text-brand-light',
  },
}

export const BANNER_STATS_TREND_TOOLTIP_TITLE = 'Compared to previous 30 days'

export const SITE_EVENT_TOTAL_EVENTS_TOOLTIP =
  'How many times visitors made a choice on this banner. Viewing the page does not count.'

export const SITE_EVENT_OPT_IN_RATE_TOOLTIP = 'The share of recorded choices that were Accept All.'

export const SITE_EVENT_ACCEPT_ALL_TOOLTIP = 'Visitors who accepted every optional cookie category.'

export const SITE_EVENT_REJECT_ALL_TOOLTIP =
  'Visitors who turned down every optional cookie category.'

/** Intro above per-category allowance counts (not a split of the Custom total). */
export const SITE_EVENT_CUSTOM_CATEGORY_TOOLTIP_INTRO =
  'How many of those visitors enabled each category:'

/** Custom tile tooltip when no visitor has made a custom choice. */
export const SITE_EVENT_CUSTOM_CLASSIFICATION_TOOLTIP_EMPTY =
  'Visitors who enabled only some cookie categories.'

/**
 * @param {{
 *   byCustomCategory: import('src/services/helpers/banners/stats.helper').EventsByCustomCategory,
 *   customCount: number,
 * }} params
 */
export function formatSiteEventCustomClassificationTooltip({ byCustomCategory, customCount }) {
  if (customCount === 0) return SITE_EVENT_CUSTOM_CLASSIFICATION_TOOLTIP_EMPTY

  const visitorLabel = customCount === 1 ? 'visitor' : 'visitors'
  const title = `${customCount} ${visitorLabel} enabled only some cookie categories.`
  const categoryLines = TRACKER_CATEGORY_OPTIONS.filter(
    (option) => option.value !== TRACKER_CATEGORIES.NECESSARY
  ).map((option) => `${option.label}: ${byCustomCategory[option.value]}`)

  return `${title}\n\n${SITE_EVENT_CUSTOM_CATEGORY_TOOLTIP_INTRO}\n${categoryLines.join('\n')}`
}

/** @param {number} optInRate Decimal rate (e.g. 0.48 → 48). */
export function roundBannerStatsOptInRatePercent(optInRate) {
  return Math.round(optInRate * 100)
}

/** @param {number} optInRate Decimal rate (e.g. 0.48 → 48%). */
export function formatBannerStatsTrendTooltipOptInRate(optInRate) {
  const percent = roundBannerStatsOptInRatePercent(optInRate)
  return `Previous period: ${percent}% opt-in rate`
}

export const BANNERS_TABLE_COLUMN_NAMES = {
  NAME_DOMAIN: 'nameDomain',
  INSTALL_STATUS: 'installStatus',
  REGULATIONS: 'regulations',
  TRACKERS: 'trackers',
  LAST_SCAN: 'lastScan',
}

/**
 * Default table state for URL sync (`getSortAndPaginationFromQuery`).
 * `sortBy` is a sortable column `name` from `BANNERS_TABLE_COLUMNS`.
 */
export const BANNERS_DEFAULT_SORT = {
  sortBy: BANNERS_TABLE_COLUMN_NAMES.LAST_SCAN,
  descending: true,
  page: 1,
  rowsPerPage: 10,
}

/** Default filter shape — `regulations` is an array in memory, serialized to comma-string for URL. */
export const BANNERS_DEFAULT_FILTER = {
  search: '',
  regulations: [],
  cookieClassification: null,
  dateRange: null,
}

/** FilterColumn options for the cookie-classification quick filter. */
export const BANNERS_COOKIE_CLASSIFICATION_FILTER_OPTIONS = [
  { value: null, label: 'Any classification', icon: 'cookie' },
  { value: TRACKER_CLASSIFICATION_STATUS.UNCLASSIFIED, label: 'Has unclassified', icon: 'cookie' },
  { value: TRACKER_CLASSIFICATION_STATUS.CLASSIFIED, label: 'All classified', icon: 'cookie' },
]

/** All valid cookie-classification values for URL `?cookieClassification` validation. */
export const VALID_COOKIE_CLASSIFICATION_KEYS = new Set(
  BANNERS_COOKIE_CLASSIFICATION_FILTER_OPTIONS.filter((opt) => opt.value !== null).map(
    (opt) => opt.value
  )
)

/** Filter + sort defaults with regulations as `null` (URL-serialized form) for `sanitizeQuery`. */
export const BANNERS_QUERY_DEFAULTS_URL = {
  ...BANNERS_DEFAULT_FILTER,
  regulations: null,
  ...BANNERS_DEFAULT_SORT,
}

export const BANNERS_TABLE_COLUMNS = [
  {
    name: BANNERS_TABLE_COLUMN_NAMES.NAME_DOMAIN,
    label: 'Name & domain',
    field: 'siteName',
    align: 'left',
    sortable: true,
    classes: 'banner-name main-td',
    headerClasses: 'banner-name main-td',
  },
  {
    name: BANNERS_TABLE_COLUMN_NAMES.TRACKERS,
    label: 'Cookies',
    field: 'totalItems',
    align: 'center',
    sortable: true,
    classes: 'trackers md-td compact-sm-td',
    headerClasses: 'trackers md-td compact-sm-td',
  },
  {
    name: BANNERS_TABLE_COLUMN_NAMES.REGULATIONS,
    label: 'Regulations',
    field: 'regulations',
    align: 'center',
    sortable: false,
    classes: 'regulations xl-td compact-lg-td q-py-xs',
    headerClasses: 'regulations xl-td compact-lg-td',
  },
  {
    name: BANNERS_TABLE_COLUMN_NAMES.INSTALL_STATUS,
    label: 'Install status',
    field: 'installStatus',
    align: 'center',
    sortable: false,
    classes: 'install-status lg-td',
    headerClasses: 'install-status lg-td',
  },
  {
    name: BANNERS_TABLE_COLUMN_NAMES.LAST_SCAN,
    label: 'Last scan',
    field: 'lastScanned',
    align: 'right',
    sortable: true,
    classes: 'last-scan lg-td',
    headerClasses: 'last-scan lg-td',
  },
]

export const NO_DATA_BANNERS = {
  empty: {
    icon: 'cookie',
    title: 'No banners yet',
    message: 'Create your first banner in minutes and start collecting consent.',
  },
  filtered: {
    icon: 'funnel-x',
    title: 'No banners match the selected filters.',
    message: 'Try adjusting or clearing your filters.',
    clearActionLabel: 'Clear filters',
  },
}
